import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { AppShell } from "@/components/app-shell";
import { fetchMapPosts } from "@/lib/posts";
import type { FeedPost } from "@/components/post-card";
import { CATEGORIES, CATEGORY_LIST, type Category } from "@/lib/categories";
import { SEVERITY_LIST, SEVERITY_META, STATUS_META, type Severity } from "@/lib/reports";
import { Loader2, MapPin } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { HotspotAlertStrip } from "@/components/hotspot-alert-strip";


export const Route = createFileRoute("/map")({
  component: MapPage,
  head: () => ({
    meta: [
      { title: "Xamseen — Civic Map" },
      { name: "description", content: "See reported problems near you on a live civic map — filter by category, severity and resolution status." },
      { property: "og:title", content: "Xamseen — Civic Map" },
      { property: "og:description", content: "Live map of community-reported problems, their severity and resolution status." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
});

type MapPost = FeedPost & { latitude: number; longitude: number };

const STATUS_FILTERS = [
  { key: "active", label: "Unresolved" },
  { key: "resolved", label: "Resolved" },
  { key: "all", label: "All statuses" },
] as const;

function MapPage() {
  const [posts, setPosts] = useState<MapPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeCats, setActiveCats] = useState<Set<Category>>(new Set());
  const [activeSevs, setActiveSevs] = useState<Set<Severity>>(new Set());
  const [statusFilter, setStatusFilter] = useState<(typeof STATUS_FILTERS)[number]["key"]>("active");
  const mapEl = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const layerRef = useRef<any>(null);

  useEffect(() => {
    fetchMapPosts().then((d) => setPosts(d as MapPost[])).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!mapEl.current || mapRef.current) return;
    let cancelled = false;
    (async () => {
      const L = (await import("leaflet")).default;
      if (cancelled || !mapEl.current) return;
      const map = L.map(mapEl.current, { center: [40, 10], zoom: 2, worldCopyJump: true });
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: "© OpenStreetMap contributors",
        maxZoom: 19,
      }).addTo(map);
      mapRef.current = map;
      layerRef.current = L.layerGroup().addTo(map);

      navigator.geolocation?.getCurrentPosition(
        (pos) => map.setView([pos.coords.latitude, pos.coords.longitude], 13),
        () => {}
      );
    })();
    return () => { cancelled = true; mapRef.current?.remove(); mapRef.current = null; };
  }, []);

  const filtered = useMemo(() => {
    return posts.filter((p) => {
      if (activeCats.size && !activeCats.has(p.category)) return false;
      const sev = (p.severity ?? "info") as Severity;
      if (activeSevs.size && !activeSevs.has(sev)) return false;
      if (statusFilter === "resolved" && p.status !== "resolved") return false;
      if (statusFilter === "active" && (p.status === "resolved" || p.status === "closed")) return false;
      return true;
    });
  }, [posts, activeCats, activeSevs, statusFilter]);

  // render markers
  useEffect(() => {
    if (!mapRef.current || !layerRef.current) return;
    (async () => {
      const L = (await import("leaflet")).default;
      layerRef.current.clearLayers();

      filtered.forEach((p) => {
        const cat = CATEGORIES[p.category];
        const sev = (p.severity ?? "info") as Severity;
        const sevMeta = SEVERITY_META[sev];
        const isEmergency = p.post_type === "emergency" || p.category === "emergency" || sev === "critical";
        const color = isEmergency ? SEVERITY_META.critical.color : sev === "info" ? cat.color : sevMeta.color;
        const size = sev === "critical" ? 34 : sev === "urgent" ? 30 : 26;
        const resolved = p.status === "resolved";
        const icon = L.divIcon({
          className: "",
          html: `<div style="
            width:${size}px;height:${size}px;border-radius:50% 50% 50% 2px;
            transform:rotate(-45deg);background:${resolved ? "#22C55E" : color};
            border:2px solid white;opacity:${resolved ? 0.7 : 1};
            box-shadow:0 2px 6px rgba(0,0,0,.25);
            display:grid;place-items:center;
          "><div style="width:8px;height:8px;background:white;border-radius:50%;transform:rotate(45deg);"></div></div>`,
          iconSize: [size, size],
          iconAnchor: [size / 2, size],
        });
        const m = L.marker([p.latitude, p.longitude], { icon });
        const esc = (t: string) => (t ?? "").replace(/[<>&]/g, (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;" }[c]!));
        m.bindPopup(`
          <div style="min-width:210px;max-width:280px">
            <div style="font-size:11px;text-transform:uppercase;letter-spacing:.04em;color:#64748B">${esc(cat.label)} · ${esc(SEVERITY_META[sev].label)}</div>
            <div style="font-weight:600;margin:4px 0 4px;line-height:1.3">${esc(p.title ?? "")}</div>
            <div style="font-size:12px;color:#64748B;margin-bottom:6px">
              ${esc(STATUS_META[p.status]?.label ?? p.status)} · ${p.verify_count ?? 0} confirmations
            </div>
            <a href="/posts/${p.id}" style="color:#2563EB;font-size:13px;font-weight:500">Open report →</a>
          </div>
        `);
        m.addTo(layerRef.current);
      });
    })();
  }, [filtered]);

  const toggle = <T,>(set: Set<T>, value: T, apply: (s: Set<T>) => void) => {
    const next = new Set(set);
    next.has(value) ? next.delete(value) : next.add(value);
    apply(next);
  };

  const hasFilters = activeCats.size > 0 || activeSevs.size > 0 || statusFilter !== "active";

  return (
    <AppShell>
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight">Civic Map</h1>
          <p className="text-sm text-muted-foreground">Reported problems, their severity and how far they've moved toward resolution.</p>
        </div>
        {hasFilters && (
          <Button variant="ghost" size="sm" onClick={() => { setActiveCats(new Set()); setActiveSevs(new Set()); setStatusFilter("active"); }}>
            Reset filters
          </Button>
        )}
      </div>

      <div className="mb-4">
        <HotspotAlertStrip onMap />
      </div>



      <div className="mb-3 flex flex-wrap gap-1.5">
        {STATUS_FILTERS.map((s) => (
          <button key={s.key} onClick={() => setStatusFilter(s.key)}
            className={cn("rounded-full border px-3 py-1.5 text-xs font-medium transition-colors",
              statusFilter === s.key ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground hover:text-foreground")}>
            {s.label}
          </button>
        ))}
        <span className="mx-1 hidden w-px bg-border sm:block" />
        {SEVERITY_LIST.map((sv) => {
          const active = activeSevs.has(sv);
          return (
            <button key={sv} onClick={() => toggle(activeSevs, sv, setActiveSevs)}
              className={cn("flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors",
                active ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground hover:text-foreground")}>
              <span className="h-2 w-2 rounded-full" style={{ background: active ? "currentColor" : SEVERITY_META[sv].color }} />
              {SEVERITY_META[sv].label}
            </button>
          );
        })}
      </div>

      <div className="mb-3 -mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
        <div className="flex w-max gap-2 pb-1">
          {CATEGORY_LIST.map((c) => {
            const active = activeCats.has(c.key);
            return (
              <button key={c.key} onClick={() => toggle(activeCats, c.key, setActiveCats)}
                className={cn("flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors",
                  active ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card text-muted-foreground hover:text-foreground")}>
                <c.icon className="h-3.5 w-3.5" style={{ color: active ? "currentColor" : c.color }} />
                {c.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="relative overflow-hidden rounded-xl border border-border bg-card">
        <div ref={mapEl} className="h-[70vh] w-full" />
        {loading && (
          <div className="absolute inset-0 grid place-items-center bg-background/60 backdrop-blur-sm">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        )}
        {!loading && filtered.length === 0 && (
          <div className="pointer-events-none absolute inset-x-0 bottom-6 mx-auto w-fit rounded-full border border-border bg-card px-4 py-2 text-xs text-muted-foreground">
            <MapPin className="mr-1 inline h-3 w-3" /> No reports match these filters.
          </div>
        )}
      </div>

      <p className="mt-3 text-xs text-muted-foreground">
        Showing {filtered.length} of {posts.length} located reports · <Link to="/report" className="text-primary hover:underline">Report a problem near you</Link>
      </p>
    </AppShell>
  );
}
